import {
  Dropdown,
  Form,
  FormItem,
  Input,
  Datepicker,
  AutoComplete,
  Container,
  CountryFilterDropdown,
} from '../../components';
import { useConversationCountries } from './useConversations';
import { useKnowledgeBaseShortList } from '../../hooks/useKnowledgeBase';
import type { FilterValueProps } from '../../components/CountryFilterDropdown';
import {
  completedConversationsOptions,
  conversationChannelOptions,
  analyticsMetricOptions,
  getAnalyticsMetricValueOptions,
  type ConversationChannel,
  MetricsWithTags,
} from './const';
import { SearchIcon } from '../../components/SearchBar/SearchIcon';

type FiltersEnum =
  | 'conversation_id'
  | 'conversation_title'
  | 'countries'
  | 'user_id'
  | 'agent_id'
  | 'started_at'
  | 'ended_at'
  | 'less_than'
  | 'more_than'
  | 'equal'
  | 'completed_conversations'
  | 'metric_name'
  | 'metric_value'
  | 'knowledge_base_id';

export type FilterProps = {
  agentsOptions: OptionType[];

  values: Record<FiltersEnum, any>;
  onChange: (newValues: Record<FiltersEnum, any>) => void;
  formId: number;
};

export const Filter: React.FC<FilterProps> = ({
  agentsOptions,
  values,
  onChange,
  formId,
}) => {
  const countries = useConversationCountries();
  const { knowledgeData: knowledgeBaseOptions } = useKnowledgeBaseShortList();
  const isOngoingConversation =
    values.completed_conversations !== undefined &&
    !values.completed_conversations;

  return (
    <div className="mb-sm grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-md xl:gap-x-24 gap-x-12">
      <Form initialValue={values} onChange={onChange} key={formId}>
        <Container>
          <FormItem
            inline={false}
            name="conversation_id"
            label="Conversation ID"
          >
            <Input />
          </FormItem>
          <FormItem
            inline={false}
            name="metric_name"
            label="Analytics Metric Name"
          >
            <Dropdown options={analyticsMetricOptions} />
          </FormItem>
          <FormItem
            inline={false}
            name="metric_value"
            label="Analytics Metric Value"
          >
            {MetricsWithTags.includes(values.metric_name) ? (
              <Input
                key={`${values.metric_name}_metric_tags`}
                placeholder="Search"
                inputClassName="pl-14 text-medium py-4"
                prefix={<SearchIcon />}
              />
            ) : (
              <Dropdown
                key={`${values.metric_name}_metric`}
                options={getAnalyticsMetricValueOptions(values.metric_name)}
              />
            )}
          </FormItem>
          <FormItem
            inline={false}
            name="knowledge_base_id"
            label="Knowledge Base"
          >
            <AutoComplete options={knowledgeBaseOptions || []} />
          </FormItem>
        </Container>

        <Container>
          <FormItem inline={false} name="user_id" label="Participant ID">
            <Input />
          </FormItem>
          <FormItem
            inline={false}
            name="agent_id"
            label="Agent"
            className="col-span-1 md:col-span-2"
          >
            <AutoComplete options={agentsOptions || []} />
          </FormItem>
          <FormItem
            inline={false}
            label="Completed status"
            name="completed_conversations"
          >
            <Dropdown<string | boolean>
              options={completedConversationsOptions}
            />
          </FormItem>
          <FormItem
            inline={false}
            label="Conversation channel"
            name="conversation_channel"
          >
            <Dropdown<string | ConversationChannel>
              options={conversationChannelOptions}
            />
          </FormItem>
        </Container>

        <Container>
          <FormItem inline={false} name="started_at" label="Started at">
            <Datepicker />
          </FormItem>
          <FormItem inline={false} name="ended_at" label="Ended at">
            <Datepicker disabled={isOngoingConversation} cleanOnDisable />
          </FormItem>
          <FormItem inline={false} name="countries" label="">
            <CountryFilterDropdown
              short
              relatedCountries={countries || []}
              value={values?.countries as FilterValueProps}
            />
          </FormItem>
          <div>
            <p className="text-gray-500 dark:text-gray-400 text-tiny mt-md">
              Duration in minutes
            </p>
            <div className="flex gap-x-sm justify-start">
              <FormItem
                inline={false}
                name="less_than"
                label="Less than"
                className="max-w-[8rem]"
              >
                <Input defaultSizeClass="max-w-[8rem]" numeric />
              </FormItem>
              <FormItem
                inline={false}
                name="more_than"
                label="More than"
                className="max-w-[8rem]"
              >
                <Input defaultSizeClass="max-w-[8rem]" numeric />
              </FormItem>
            </div>
          </div>
        </Container>
      </Form>
    </div>
  );
};
